// File: src/components/trends/TrendsMenu.js

import React, { useState, useEffect, useRef } from "react";
import { motion, useAnimation } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useProfile } from "../../context/ProfileContext";
import ProfileLink from '../profile/ProfileLink';
import ComingSoonModal from '../miscellaneous/ComingSoonModal';
import usePressAndHold from '../../hooks/usePressAndHold';
import ReactionSelector from '../music/ReactionSelector';

import likeIcon from "../../assets/trends_like.png";
import likedIcon from "../../assets/trends_liked.png";
import dislikedIcon from "../../assets/trends_disliked.png";
import saveIcon from "../../assets/trends_save.png";
import shareIcon from "../../assets/trends_share.png";
import subscribeIcon from '../../assets/subscribe_white.png';
import subscribedIcon from '../../assets/subscribe_colour.png';

const TrendsMenu = ({ 
    currentSong, 
    rating, 
    animationControls, 
    onOpenPlaylistModal,
    isSubscribed, 
    toggleSubscription,
    onRate,
    originRef
}) => {
  // 🌟 Read global light/dark mode state directly from ProfileContext
  const { isDarkMode } = useProfile(); 
  
  const saveControls = useAnimation();
  const subscribeControls = useAnimation();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isReactionOpen, setIsReactionOpen] = useState(false);

  const likeRef = useRef(null);
  const closeTimerRef = useRef(null);

  useEffect(() => {
    if (likeRef.current && originRef) {
      originRef.current = likeRef.current;
    }
  }, [originRef, currentSong?.id]);

  const cancelCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };


  const startCloseTimer = () => {
    cancelCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setIsReactionOpen(false);
    }, 300); // 300ms grace window to move cursor into popover
  };

  const handleRate = async (newRating) => {
      if (!currentSong || !onRate) return;
      
      // Trigger scaling animation on rating button
      if (animationControls) {
          animationControls.start({
              scale: [1, 1.4, 1],
              transition: { duration: 0.3 }
          });
      }

      // 🔑 Calls handleRating in TrendsPage.js to launch credit animation overlay!
      await onRate(newRating);
  };

  const handleQuickTapToggle = () => {
      const nextRating = rating === "liked" ? "disliked" : "liked";
      handleRate(nextRating);
  };

  const gestureHandlers = usePressAndHold(
      () => setIsReactionOpen(true),
      handleQuickTapToggle,
      300
  );

  const handlePlaylistClick = async () => {
    saveControls.start({
      scale: [1, 1.4, 1],
      transition: { duration: 0.3 },
    });
    
    if (onOpenPlaylistModal) {
      onOpenPlaylistModal(currentSong);
    }
  };

  const handleSubscribeToggle = () => {
      if (currentSong?.artistId && toggleSubscription) {
          subscribeControls.start({
              scale: [1, 1.4, 1],
              transition: { duration: 0.3 }
          });
          toggleSubscription(currentSong.artistId, currentSong.artistName, 'trends_menu');
      }
  };

  return (
    <>
      <div className="fixed top-[33dvh] right-4 flex flex-col justify-between h-[50dvh] items-center pointer-events-auto z-40">
        
        <ProfileLink 
            profileId={currentSong?.artistId}
            photoURL={currentSong?.artistProfileURL}
            altText={currentSong?.artistName}
            source="trends_menu"
        />

        {/* 🌟 RATING BUTTON CONTAINER WITH HOVER GRACE PERIOD HANDLERS */}
        <div 
            className="relative flex items-center justify-center"
            onMouseEnter={cancelCloseTimer}
            onMouseLeave={startCloseTimer} // 🔑 Starts 300ms grace period timer
        >
            <ReactionSelector 
                isOpen={isReactionOpen}
                onClose={() => setIsReactionOpen(false)}
                onSelectReaction={(selected) => handleRate(selected)}
                onMouseEnter={cancelCloseTimer} // 🔑 Cancels timer when cursor reaches popover
                isDarkMode={isDarkMode}
                position="left"
            />

            <motion.button 
                {...gestureHandlers} 
                animate={animationControls}
                className="p-1 rounded-full flex items-center justify-center focus:outline-none"
            >
                <img
                    ref={likeRef}
                    src={
                        rating === "liked"
                        ? likedIcon
                        : rating === "disliked"
                        ? dislikedIcon
                        : likeIcon
                    }
                    alt="Like/Dislike"
                    className="w-8 h-8 opacity-80 cursor-pointer object-contain"
                />
            </motion.button>
        </div>

        <motion.button onClick={handleSubscribeToggle} animate={subscribeControls} type="button">
            <img 
                src={isSubscribed ? subscribedIcon : subscribeIcon} 
                alt="Subscribe"
                className="w-8 h-8 opacity-80 cursor-pointer object-contain"
            />
        </motion.button>

        <motion.img
          src={saveIcon}
          onClick={handlePlaylistClick}
          className="w-8 h-8 opacity-80 cursor-pointer object-contain"
          animate={saveControls}
          alt="Add to Playlist"
        />

        <img 
            src={shareIcon} 
            className="w-8 h-8 opacity-80 cursor-pointer object-contain"
            onClick={() => setIsModalOpen(true)}
            alt="Share"
        />
      </div>

      <ComingSoonModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
};

export default TrendsMenu;